import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { DEFAULT_PAYMENT_TRACKING_AUTHORITATIVE_FROM } from "@wmf/shared/studentPaymentTracking";
import { useEffect, useState, type FormEvent } from "react";
import { get, send } from "../api.js";
import { isOwner, isStaff, useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";
import { TeacherPaymentTracking, type TeacherPaymentTrackingRead } from "./TeacherPaymentTracking.js";

interface PaymentTrackingRead extends Partial<TeacherPaymentTrackingRead> {
  payment_tracking_authoritative_from: string | null;
}

interface Props {
  studentId: string;
  refreshKey?: number;
}

export function PaymentTrackingAuthority({ studentId, refreshKey = 0 }: Props) {
  const publish = usePublishActionOutcome();
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const { me } = useAuth();
  // Payment tracking is available to staff independently of owner-only contact
  // detail edits. The API rechecks access to this student on every read/write.
  const canManage = isStaff(me);
  const { data, loading, error, reload } = useAsync(
    () =>
      canManage
        ? get<PaymentTrackingRead>(`/admin/students/${studentId}/payment-tracking`)
        : Promise.resolve<PaymentTrackingRead>({ payment_tracking_authoritative_from: null }),
    [studentId, canManage, refreshKey],
  );
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setValue(data?.payment_tracking_authoritative_from ?? "");
  }, [data, editing]);

  if (!canManage) return null;
  if (data?.trackers && data.default_authoritative_from !== undefined) {
    return <TeacherPaymentTracking
      key={studentId} studentId={studentId}
      data={data as TeacherPaymentTrackingRead}
      owner={isOwner(me)}
      viewerId={me?.user?.id ?? ""}
      reload={reload}
    />;
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setProblem(null);
    try {
      const saved = await send<PaymentTrackingRead>(
        "PUT",
        `/admin/students/${studentId}/payment-tracking`,
        { payment_tracking_authoritative_from: value || null },
      );
      const result: ActionOutcome = { variant: "success", title: "Payment tracking date saved", details: [{ value: saved.payment_tracking_authoritative_from ?? "Automatic — all available history" }] };
      if (!publish(result)) setOutcome(result);
      setEditing(false);
      await reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not save payment tracking date.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      {outcome && <ActionConfirmation {...outcome} />}
      <div className="row" style={{ justifyContent: "space-between", gap: 16 }}>
        <div>
          <strong>Payment tracking</strong>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            Credits automatically settle the oldest eligible unpaid lessons. By default, all Amelia
            history available to the Hub is included; set a later date only when older lessons should
            be protected from automatic allocation.
          </p>
        </div>
        {!editing && !loading && !error && (
          <button className="quiet small" type="button" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {loading && <p className="small muted">Loading…</p>}
      {error && <div className="error small">{error}</div>}

      {!loading && !error && !editing && (
        <p style={{ marginBottom: 0 }}>
          <span className="small muted">Authoritative from</span><br />
          {data?.payment_tracking_authoritative_from ?? (
            <span className="muted">
              Automatic — all available history (from 20 July 2026)
            </span>
          )}
        </p>
      )}

      {editing && (
        <form onSubmit={save} style={{ marginTop: 12 }}>
          {problem && <div className="error small">{problem}</div>}
          <label>
            <span className="small muted">Authoritative from</span>
            <input
              type="date"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </label>
          <p className="small muted">
            Leaving this blank uses all Amelia history available to the Hub, from 20 July 2026.
            Choose a later date only to stop older legacy lessons consuming newly-added credits.
          </p>
          <div className="row" style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="quiet"
              disabled={saving}
              onClick={() => setValue(DEFAULT_PAYMENT_TRACKING_AUTHORITATIVE_FROM)}
            >
              Use all available history (from 20 July 2026)
            </button>
          </div>
          <div className="row">
            <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button
              type="button"
              className="quiet"
              disabled={saving}
              onClick={() => {
                setEditing(false);
                setProblem(null);
                setValue(data?.payment_tracking_authoritative_from ?? "");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
